import { readStore, writeStore } from "../lib/storage";

/** Where data comes from. `?mock=1|slow|fail|offline` switches the app to the in-browser mock
 * backend (same ApiClient interface) so every state is reachable without a server:
 * - `slow` holds loading states for seconds;
 * - `fail` returns the error envelope on data calls (sign-in still works);
 * - `offline` makes every call fail like a dropped network (the mock also does this whenever
 *   the browser itself is offline), which is how the offline queue is exercised.
 * `?mock=0` returns to HTTP. `?mock=reset` restores the seed data. Kept for the tab, because
 * in-app links drop the query string. */
export type DataMode = "http" | "mock" | "slow" | "fail" | "offline";

const KEY = "pos.dataMode";
const isMode = (v: unknown): v is DataMode => v === "http" || v === "mock" || v === "slow" || v === "fail" || v === "offline";

export function parseMockParam(value: string | null): DataMode | "reset" | null {
  if (value === null) return null;
  switch (value.trim().toLowerCase()) {
    case "":
    case "1":
    case "true":
    case "on":
      return "mock";
    case "slow":
      return "slow";
    case "fail":
      return "fail";
    case "offline":
      return "offline";
    case "reset":
      return "reset";
    case "0":
    case "false":
    case "off":
      return "http";
    default:
      return null;
  }
}

/** `?mock=` only in dev or builds that opt in (see pos-web). */
export const mockSwitchAllowed = (): boolean => import.meta.env.DEV || import.meta.env.VITE_ALLOW_MOCK === "true" || import.meta.env.VITE_DATA_SOURCE === "mock";

export function resolveDataMode(search: string = window.location.search, envDefault: string | undefined = import.meta.env.VITE_DATA_SOURCE): { mode: DataMode; reset: boolean } {
  const fromUrl = mockSwitchAllowed() ? parseMockParam(new URLSearchParams(search).get("mock")) : null;
  if (fromUrl === "reset") {
    writeStore("session", KEY, "mock");
    return { mode: "mock", reset: true };
  }
  if (fromUrl) {
    writeStore("session", KEY, fromUrl);
    return { mode: fromUrl, reset: false };
  }
  const fallback: DataMode = envDefault === "mock" ? "mock" : "http";
  return { mode: mockSwitchAllowed() ? readStore("session", KEY, fallback, isMode) : fallback, reset: false };
}

export const isMockMode = (m: DataMode): boolean => m !== "http";
